import { useState } from "react";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  CompanyNote,
  CompanyNoteKind,
  CompanyNoteTeam,
  Sale,
} from "../types";
import { Segmented } from "./Segmented";

const PAGE_SIZE = 25;
const TEAMS: CompanyNoteTeam[] = ["sales", "technical", "marketing"];

/** Company activity log (the "fichas" of the old CRM), newest first. */
export const CompanyNotesTab = ({ companyId }: { companyId: Identifier }) => {
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const translate = useTranslate();
  const { data, total, isPending, refetch } = useGetList<CompanyNote>(
    "company_notes",
    {
      filter: { company_id: companyId },
      sort: { field: "date", order: "DESC" },
      pagination: { page: 1, perPage },
    },
  );
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const salesById = new Map((sales ?? []).map((s) => [s.id, s]));

  return (
    <div className="flex flex-col gap-4 pt-2">
      <CompanyNoteForm companyId={companyId} onCreated={refetch} />
      {isPending ? null : !data?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.construnario.company_notes.empty")}
        </p>
      ) : (
        <ul className="flex flex-col divide-y">
          {data.map((note) => (
            <CompanyNoteItem
              key={note.id}
              note={note}
              author={note.sales_id ? salesById.get(note.sales_id) : undefined}
            />
          ))}
        </ul>
      )}
      {total != null && data && data.length < total ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPerPage((n) => n + PAGE_SIZE)}
          >
            {translate("crm.construnario.company_notes.load_more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const CompanyNoteItem = ({
  note,
  author,
}: {
  note: CompanyNote;
  author?: Sale;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const authorName = author
    ? `${author.first_name} ${author.last_name}`.trim()
    : note.legacy_author;

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {new Date(note.date).toLocaleDateString(locale, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
        {note.kind ? (
          <Badge variant="outline">
            {translate(`crm.construnario.company_notes.kind.${note.kind}`)}
          </Badge>
        ) : null}
        {note.team !== "sales" ? (
          <Badge variant="secondary">
            {translate(`crm.construnario.company_notes.team.${note.team}`)}
          </Badge>
        ) : null}
        {note.outcome ? <Badge variant="outline">{note.outcome}</Badge> : null}
        {authorName ? (
          <span>
            {translate("crm.construnario.company_notes.by", {
              name: authorName,
            })}
          </span>
        ) : null}
      </div>
      {note.text ? (
        // Legacy notes contain <br> line breaks; render them as plain text lines.
        <p className="mt-1 whitespace-pre-line text-sm">
          {note.text.replace(/<br\s*\/?>/gi, "\n")}
        </p>
      ) : null}
    </li>
  );
};

type FollowUp = "none" | "tomorrow" | "week" | "month";

const KINDS: CompanyNoteKind[] = ["call", "email", "visit", "note"];
const FOLLOW_UPS: FollowUp[] = ["none", "tomorrow", "week", "month"];
const FOLLOW_UP_DAYS: Record<Exclude<FollowUp, "none">, number> = {
  tomorrow: 1,
  week: 7,
  month: 30,
};
// Atomic task types (root/defaultConfiguration.ts) closest to each kind.
const TASK_TYPE: Record<CompanyNoteKind, string> = {
  call: "call",
  email: "email",
  visit: "meeting",
  note: "follow-up",
  contract: "follow-up",
};

const followUpDate = (followUp: Exclude<FollowUp, "none">) => {
  const date = new Date();
  date.setDate(date.getDate() + FOLLOW_UP_DAYS[followUp]);
  date.setHours(9, 0, 0, 0);
  return date.toISOString();
};

/**
 * Log an activity: what kind it was, what happened and, optionally, when to
 * follow up. The follow-up becomes a task on the company, which replaces the
 * single "appointment" per company that the old CRM kept overwriting.
 */
const CompanyNoteForm = ({
  companyId,
  onCreated,
}: {
  companyId: Identifier;
  onCreated: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [text, setText] = useState("");
  const [kind, setKind] = useState<CompanyNoteKind>("call");
  const [followUp, setFollowUp] = useState<FollowUp>("none");
  const [team, setTeam] = useState<CompanyNoteTeam>("sales");
  const [create, { isPending }] = useCreate();

  if (!identity) return null;

  const fail = (error: unknown) =>
    notify(error instanceof Error ? error.message : String(error), {
      type: "error",
    });

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    create(
      "company_notes",
      {
        data: {
          company_id: companyId,
          sales_id: identity.id,
          team,
          kind,
          text: trimmed,
          date: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => {
          setText("");
          onCreated();
          if (followUp === "none") {
            notify("crm.construnario.company_notes.added");
            return;
          }
          create(
            "tasks",
            {
              data: {
                company_id: companyId,
                contact_id: null,
                sales_id: identity.id,
                type: TASK_TYPE[kind],
                text: trimmed.slice(0, 200),
                due_date: followUpDate(followUp),
              },
            },
            {
              onSuccess: () => {
                notify("crm.construnario.company_notes.added_with_follow_up", {
                  messageArgs: {
                    when: translate(
                      `crm.construnario.company_notes.follow_up.${followUp}`,
                    ).toLowerCase(),
                  },
                });
                setFollowUp("none");
              },
              onError: fail,
            },
          );
        },
        onError: fail,
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <Segmented
        label={translate("crm.construnario.company_notes.kind_label")}
        value={kind}
        onChange={setKind}
        options={KINDS.map((k) => ({
          value: k,
          label: translate(`crm.construnario.company_notes.kind.${k}`),
        }))}
      />
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={translate("crm.construnario.company_notes.placeholder")}
        aria-label={translate("crm.construnario.company_notes.placeholder")}
        rows={3}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-muted-foreground">
          {translate("crm.construnario.company_notes.follow_up_label")}
        </span>
        <Segmented
          label={translate("crm.construnario.company_notes.follow_up_label")}
          value={followUp}
          onChange={setFollowUp}
          options={FOLLOW_UPS.map((f) => ({
            value: f,
            label: translate(`crm.construnario.company_notes.follow_up.${f}`),
          }))}
        />
        <div className="ml-auto flex items-center gap-2">
          <Select
            value={team}
            onValueChange={(v) => setTeam(v as CompanyNoteTeam)}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAMS.map((t) => (
                <SelectItem key={t} value={t}>
                  {translate(`crm.construnario.company_notes.team.${t}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={submit} disabled={isPending || !text.trim()}>
            {translate("crm.construnario.company_notes.add")}
          </Button>
        </div>
      </div>
    </div>
  );
};
